import {useEffect} from 'react';

import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {PublishingRecordForVersionHistoryPanel} from 'template-collectibles/publishing-ui/graphql/types';

import {ProcedureFlowchart, ProcedureMetadata} from '../ProcedureDomainTypes';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from '../utils/ProcedureCapabilityManagementHelpers';
import getBannerForReadOnlyProcedure from './getBannerForReadOnlyProcedure';
import {ProcedureAccessLevel} from './validation/determineProcedureAccessLevel';

export interface UseProcedureReadonlyStateParams {
  draftFlowchart: ProcedureFlowchart;
  procedureMetadata: ProcedureMetadata;
  procedureId: string;
  accessLevel: ProcedureAccessLevel | null;
  fullyBlockedOnServerError: boolean;
  currentFlowchartVersionMetadata: {id: string; createdAt: string; isLatest: boolean};
  publishingRecords: Array<PublishingRecordForVersionHistoryPanel>;
  readonlyState: [boolean, (isReadonly: boolean) => void];
  isAutosaverBlockedState: [boolean, (isBlocked: boolean) => void];
  isSaveInFlightState: [boolean, (isSaveInFlight: boolean) => void];
}

export interface UseProcedureReadonlyStateReturn {
  readOnlyBannerMessage: string | null;
  isProcedureReadOnly: boolean;
  isProcedureArchived: boolean;
}

/**
 * Hook that manages the read-only state of a procedure, including:
 * - Computing whether the procedure should be read-only
 * - Generating the appropriate banner message for read-only states
 * - Managing autosave blocking based on read-only conditions
 */
export default function useProcedureReadonlyState({
  draftFlowchart,
  procedureMetadata,
  procedureId,
  accessLevel,
  fullyBlockedOnServerError,
  currentFlowchartVersionMetadata,
  publishingRecords,
  readonlyState,
  isAutosaverBlockedState,
  isSaveInFlightState,
}: UseProcedureReadonlyStateParams): UseProcedureReadonlyStateReturn {
  const [isProcedureReadOnly, setProcedureReadonly] = readonlyState;
  const [_shouldBlockAutosave, setShouldBlockAutosave] = isAutosaverBlockedState;
  const [_isSaving, setIsSaving] = isSaveInFlightState;

  const isProcedureArchived = isArchived(procedureMetadata);
  const hasAccessToEditConfig = isFeatureEnabledForProcedureType(
    procedureMetadata.procedureType,
    ProceduresFeature.EDIT_CONFIG
  );
  const isViewingPublishedVersion =
    BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING &&
    null != publishingRecords.find((pr) => pr.publishableVersionId === currentFlowchartVersionMetadata.id);

  const readOnlyBannerMessage = getBannerForReadOnlyProcedure({
    procedureType: procedureMetadata.procedureType,
    fullyBlockedOnServerError,
    isLatestFlowchartVersion: currentFlowchartVersionMetadata.isLatest,
    isViewingPublishedVersion,
    isProcedureArchived,
    isWritable: procedureMetadata.permissions.writable,
    hasAccessToEditConfig,
    accessLevel,
  });
  const newIsProcedureReadOnly = readOnlyBannerMessage != null;

  // Update readonly state when it changes
  if (isProcedureReadOnly !== newIsProcedureReadOnly) {
    setProcedureReadonly(newIsProcedureReadOnly);
  }

  // Manage autosave blocking based on various conditions
  useEffect(() => {
    if (fullyBlockedOnServerError) {
      setIsSaving(false);
    }
    setShouldBlockAutosave(
      fullyBlockedOnServerError || isProcedureArchived || !hasAccessToEditConfig || isProcedureReadOnly
    );
  }, [
    fullyBlockedOnServerError,
    isProcedureArchived,
    hasAccessToEditConfig,
    isProcedureReadOnly,
    setIsSaving,
    setShouldBlockAutosave,
  ]);

  return {
    readOnlyBannerMessage,
    isProcedureReadOnly: newIsProcedureReadOnly,
    isProcedureArchived,
  };
}
